import React, { useRef, useState } from 'react';
import { Button, Dropdown, Input, Label, Table } from 'semantic-ui-react';
import {
    EProgrammingTravelType,
    Programming,
    travelTypeOptions,
    UpdateProgrammingRequest,
} from "../../models/programming";
import { ShortDto } from "../../models/short-dto";
import { ProgrammingLocationDto } from "../../models/programming-location-dto";
import { toDatetimeLocalValue } from "../../utils/programming-timeline-utils";
import { formatProgrammingDateTimeCompact } from '../../utils/programming-timeline-utils';
import { getTripClosureColumnLabel } from '../../utils/programming-trip-closure';
import { getStatusLabelColor, getTrackingStatusLabel } from '../../shared/utils/torre-tracking-utils';
import './torre-table-ux.css';
import { TorreOperationalHighlightColumn, TorreOperationalSection } from './torre-operational-groups';
import {
    canEditSpreadsheetRow,
    SPREADSHEET_COLUMN_COUNT,
    SPREADSHEET_COLUMN_KEYS,
    SPREADSHEET_COLUMN_LABELS,
    SPREADSHEET_COLUMN_WIDTHS,
    SPREADSHEET_INLINE_STATUS_OPTIONS,
    SPREADSHEET_TABLE_MIN_WIDTH,
    toUpdateProgrammingRequest,
} from "./programming-list-spreadsheet";

export interface SpreadsheetLookups {
    vehiclePlates: ShortDto[];
    clients: ShortDto[];
    drivers: ShortDto[];
    products: ShortDto[];
    references: ShortDto[];
    routes?: ShortDto[];
}

interface ProgrammingSpreadsheetTableProps {
    programmings: Programming[];
    lookups: SpreadsheetLookups;
    canInlineEdit: boolean;
    isManager?: boolean;
    lockedClientId?: string;
    savingId?: string | null;
    onSelect?: (programming: ProgrammingLocationDto) => void;
    onOpenDetails: (programming: Programming, event?: React.MouseEvent) => void;
    onOpenEdit?: (programming: Programming, event?: React.MouseEvent) => void;
    onSaveRow: (id: string, payload: UpdateProgrammingRequest) => Promise<void>;
    editUserId?: string;
    editUserEmail?: string;
    editProfile?: string;
    torreUxEnhanced?: boolean;
    selectedProgrammingId?: string | null;
    operationalSections?: TorreOperationalSection[];
}

type EditableField =
    | 'mainVehiclePlateId'
    | 'semiTrailer1Id'
    | 'semiTrailer2Id'
    | 'volume'
    | 'clientId'
    | 'travelType'
    | 'status'
    | 'driverId'
    | 'originReferenceId'
    | 'destinationReferenceId'
    | 'productId'
    | 'expectedDestinationArrivalAt'
    | 'expectedOriginDepartureAt';

const HEADER_STYLE: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: '#f9fafb',
    whiteSpace: 'nowrap',
    fontSize: '0.82em',
};

const toOptions = (items: ShortDto[]) =>
    items.map(item => ({ key: item.key, text: item.text, value: item.value }));

const ProgrammingSpreadsheetTable: React.FC<ProgrammingSpreadsheetTableProps> = ({
    programmings,
    lookups,
    canInlineEdit,
    isManager = false,
    lockedClientId,
    savingId,
    onSelect,
    onOpenDetails,
    onOpenEdit,
    onSaveRow,
    editUserId,
    editUserEmail,
    editProfile,
    torreUxEnhanced = false,
    selectedProgrammingId = null,
    operationalSections,
}) => {
    const [editing, setEditing] = useState<{ id: string; field: EditableField } | null>(null);
    const [draft, setDraft] = useState<string | number>('');
    const committingRef = useRef(false);

    const plateOptions = toOptions(lookups.vehiclePlates);
    const clientOptions = toOptions(lookups.clients);
    const driverOptions = toOptions(lookups.drivers);
    const productOptions = toOptions(lookups.products);
    const referenceOptions = toOptions(lookups.references);

    const rowEditable = (programming: Programming) => canEditSpreadsheetRow({
        canInlineEdit,
        isManager,
        lockedClientId,
        clientId: programming.clientId,
        status: programming.status,
        profile: editProfile,
        userId: editUserId,
        userEmail: editUserEmail,
        createdByUserId: programming.createdByUserId,
        createdBy: programming.createdBy,
    });

    const startEdit = (programming: Programming, field: EditableField, current: string | number) => {
        if (!rowEditable(programming)) {
            return;
        }
        if (field === 'clientId' && lockedClientId) {
            return;
        }
        setEditing({ id: programming.id, field });
        setDraft(current ?? '');
    };

    const cancelEdit = () => {
        committingRef.current = false;
        setEditing(null);
        setDraft('');
    };

    const commit = async (programming: Programming, field: EditableField, value: string | number) => {
        if (committingRef.current) return;
        committingRef.current = true;
        const patch: Partial<UpdateProgrammingRequest> = { [field]: value === '' ? undefined : value };
        if (field === 'expectedOriginDepartureAt') {
            patch.expectedDepartureTime = value ? String(value) : undefined;
        }
        if (field === 'expectedDestinationArrivalAt') {
            patch.expectedArrivalTime = value ? String(value) : undefined;
        }
        try {
            await onSaveRow(programming.id, toUpdateProgrammingRequest(programming, {
                ...patch,
                auditSource: 'torre-spreadsheet-inline',
            }));
        } finally {
            cancelEdit();
        }
    };

    const renderStatusCell = (programming: Programming, current?: string | number) => {
        const isEditing = editing?.id === programming.id && editing.field === 'status';
        if (isEditing) {
            return (
                <Dropdown
                    selection
                    search
                    fluid
                    compact
                    clearable
                    options={SPREADSHEET_INLINE_STATUS_OPTIONS}
                    value={draft}
                    data-testid={`spreadsheet-editor-${programming.id}-status`}
                    onChange={async (_, { value }) => {
                        await commit(programming, 'status', value as string | number);
                    }}
                    onClick={(event: React.MouseEvent) => event.stopPropagation()}
                />
            );
        }
        const statusLabel = getTrackingStatusLabel(current ?? programming.status);
        const editable = rowEditable(programming);
        return (
            <span
                data-testid={`spreadsheet-cell-${programming.id}-status`}
                onClick={() => startEdit(programming, 'status', current ?? '')}
                style={{ cursor: editable ? 'pointer' : 'default', display: 'block', minHeight: 22 }}
                className="torre-spreadsheet-status-pill"
            >
                {torreUxEnhanced ? (
                    <Label color={getStatusLabelColor(programming.status)} size="tiny">
                        {statusLabel}
                    </Label>
                ) : (
                    statusLabel
                )}
            </span>
        );
    };

    const renderDropdown = (
        programming: Programming,
        field: EditableField,
        options: { key: string | number; text: string; value: string | number }[],
        current?: string | number
    ) => {
        if (field === 'status') {
            return renderStatusCell(programming, current);
        }
        const isEditing = editing?.id === programming.id && editing.field === field;
        const label = options.find(option => String(option.value) === String(current))?.text
            || (current ? String(current) : '—');

        if (!isEditing) {
            return (
                <span
                    data-testid={`spreadsheet-cell-${programming.id}-${field}`}
                    onClick={() => startEdit(programming, field, current ?? '')}
                    style={{ cursor: rowEditable(programming) ? 'pointer' : 'default', display: 'block', minHeight: 22 }}
                    title={label}
                >
                    {label}
                </span>
            );
        }

        return (
            <Dropdown
                selection
                search
                fluid
                compact
                clearable
                options={options}
                value={draft}
                data-testid={`spreadsheet-editor-${programming.id}-${field}`}
                onChange={async (_, { value }) => {
                    await commit(programming, field, value as string | number);
                }}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
            />
        );
    };

    const renderInput = (
        programming: Programming,
        field: EditableField,
        current: string | number,
        type: 'number' | 'datetime-local'
    ) => {
        const isEditing = editing?.id === programming.id && editing.field === field;
        const display = type === 'datetime-local'
            ? (current ? formatProgrammingDateTimeCompact(String(current)) : '—')
            : (current === '' || current == null ? '—' : String(current));

        if (!isEditing) {
            return (
                <span
                    data-testid={`spreadsheet-cell-${programming.id}-${field}`}
                    onClick={() => startEdit(programming, field, current ?? '')}
                    style={{ cursor: rowEditable(programming) ? 'pointer' : 'default', display: 'block', minHeight: 22 }}
                >
                    {display}
                </span>
            );
        }

        const valueFromEvent = (raw: string) => (type === 'number' ? (raw === '' ? '' : Number(raw)) : raw);

        return (
            <Input
                type={type}
                value={draft}
                fluid
                data-testid={`spreadsheet-editor-${programming.id}-${field}`}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                onChange={(_, { value }) => setDraft(valueFromEvent(String(value)))}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    commit(programming, field, valueFromEvent(event.target.value));
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        commit(programming, field, valueFromEvent((event.target as HTMLInputElement).value));
                    }
                    if (event.key === 'Escape') {
                        cancelEdit();
                    }
                }}
            />
        );
    };

    const toLocationDto = (programming: Programming): ProgrammingLocationDto => ({
        ...(programming as unknown as ProgrammingLocationDto),
        currentLatitude: programming.currentLatitude,
        currentLongitude: programming.currentLongitude,
        currentAddressInfo: programming.currentAddressInfo,
        expectedArrivalTime: programming.expectedArrivalTime,
        expectedDepartureTime: programming.expectedDepartureTime,
        expectedOriginDepartureAt: programming.expectedOriginDepartureAt,
        expectedDestinationArrivalAt: programming.expectedDestinationArrivalAt,
        expectedDestinationDepartureAt: programming.expectedDestinationDepartureAt,
        expectedReturnArrivalAt: programming.expectedReturnArrivalAt,
        actualOriginExitedAt: programming.actualOriginExitedAt,
        actualDestinationEnteredAt: programming.actualDestinationEnteredAt,
        actualDestinationExitedAt: programming.actualDestinationExitedAt,
        actualReturnEnteredAt: programming.actualReturnEnteredAt,
        isGoAndReturn: programming.isGoAndReturn ?? programming.isReturn,
        productName: programming.productName,
        volume: programming.volume,
        travelType: String(programming.travelType),
    });

    const highlightSpreadsheetCell = (
        columnKey: 'prevSaida' | 'prevDescarga',
        highlight?: TorreOperationalHighlightColumn
    ) => {
        if (highlight === 'prevSaida' && columnKey === 'prevSaida') return 'torre-col-highlight';
        if (highlight === 'prevCheg' && columnKey === 'prevDescarga') return 'torre-col-highlight';
        return undefined;
    };

    const renderGroupHeader = (section: TorreOperationalSection) => (
        <Table.Row key={`sheet-group-${section.key}`} className="torre-group-header-row">
            <Table.Cell colSpan={String(SPREADSHEET_COLUMN_COUNT)}>
                {section.label} • {section.rows.length}
            </Table.Cell>
        </Table.Row>
    );

    const renderProgrammingRow = (
        programming: Programming,
        highlight?: TorreOperationalHighlightColumn
    ) => {
        const expectedArrival = toDatetimeLocalValue(
            programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime
        ) ?? '';
        const expectedDeparture = toDatetimeLocalValue(
            programming.expectedOriginDepartureAt ?? programming.expectedDepartureTime
        ) ?? '';
        const travelTypeValue = typeof programming.travelType === 'number'
            ? programming.travelType
            : programming.travelType === 'Loaded'
                ? EProgrammingTravelType.Loaded
                : EProgrammingTravelType.Empty;

        const isSelected = selectedProgrammingId === programming.id;
        const closureLabel = getTripClosureColumnLabel(programming);
        return (
            <Table.Row
                key={programming.id}
                disabled={savingId === programming.id}
                className={isSelected && torreUxEnhanced ? 'torre-row--selected' : undefined}
                onClick={() => onSelect?.(toLocationDto(programming))}
                style={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
                <Table.Cell className={torreUxEnhanced ? 'torre-table-sticky-first-col' : undefined}>
                    {renderDropdown(programming, 'mainVehiclePlateId', plateOptions, programming.mainVehiclePlateId)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'semiTrailer1Id', plateOptions, programming.semiTrailer1Id)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'semiTrailer2Id', plateOptions, programming.semiTrailer2Id)}
                </Table.Cell>
                <Table.Cell>
                    {renderInput(programming, 'volume', programming.volume, 'number')}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'clientId', clientOptions, programming.clientId)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'travelType', travelTypeOptions, travelTypeValue)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'status', SPREADSHEET_INLINE_STATUS_OPTIONS, programming.status as number)}
                </Table.Cell>
                <Table.Cell title={closureLabel}>
                    <span style={{ fontSize: '0.85em' }}>{closureLabel}</span>
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'driverId', driverOptions, programming.driverId)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'originReferenceId', referenceOptions, programming.originReferenceId)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'destinationReferenceId', referenceOptions, programming.destinationReferenceId)}
                </Table.Cell>
                <Table.Cell>
                    {renderDropdown(programming, 'productId', productOptions, programming.productId)}
                </Table.Cell>
                <Table.Cell className={highlightSpreadsheetCell('prevDescarga', highlight)}>
                    {renderInput(programming, 'expectedDestinationArrivalAt', expectedArrival, 'datetime-local')}
                </Table.Cell>
                <Table.Cell className={highlightSpreadsheetCell('prevSaida', highlight)}>
                    {renderInput(programming, 'expectedOriginDepartureAt', expectedDeparture, 'datetime-local')}
                </Table.Cell>
                <Table.Cell
                    textAlign="center"
                    onClick={(event: React.MouseEvent) => event.stopPropagation()}
                >
                    <Button.Group size="mini">
                        {onOpenEdit && (
                            <Button
                                type="button"
                                color="blue"
                                icon="edit"
                                title={rowEditable(programming) ? 'Editar' : 'Sem permissão para editar'}
                                disabled={!rowEditable(programming)}
                                data-testid={`spreadsheet-edit-${programming.id}`}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (rowEditable(programming)) onOpenEdit(programming, event);
                                }}
                            />
                        )}
                        <Button
                            type="button"
                            color="teal"
                            icon="eye"
                            title="Ver detalhes"
                            data-testid={`spreadsheet-details-${programming.id}`}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onOpenDetails(programming, event);
                            }}
                        />
                    </Button.Group>
                </Table.Cell>
            </Table.Row>
        );
    };

    const headerCellStyle = (key: typeof SPREADSHEET_COLUMN_KEYS[number]): React.CSSProperties => ({
        ...HEADER_STYLE,
        ...(torreUxEnhanced ? { background: '#f3f4f6', fontWeight: 600 } : {}),
    });

    return (
        <div
            className={torreUxEnhanced ? 'torre-table-scroll torre-table-scroll--spreadsheet' : undefined}
            style={torreUxEnhanced ? undefined : { overflow: 'auto', width: '100%', maxHeight: '70vh' }}
            data-testid="torre-spreadsheet"
        >
            <Table
                celled
                striped
                compact
                color="green"
                className={torreUxEnhanced ? 'torre-table-ux' : undefined}
                style={{ tableLayout: 'fixed', width: '100%', minWidth: SPREADSHEET_TABLE_MIN_WIDTH }}
            >
                <colgroup>
                    {SPREADSHEET_COLUMN_WIDTHS.map((width, index) => (
                        <col key={`spreadsheet-col-${index}`} style={{ width }} />
                    ))}
                </colgroup>
                <Table.Header>
                    <Table.Row>
                        {SPREADSHEET_COLUMN_KEYS.map((key) => (
                            <Table.HeaderCell
                                key={key}
                                style={headerCellStyle(key)}
                                className={[
                                    torreUxEnhanced ? 'torre-table-sticky-header-cell' : '',
                                    torreUxEnhanced && key === 'placa' ? 'torre-table-sticky-first-col' : '',
                                ].filter(Boolean).join(' ') || undefined}
                                title={SPREADSHEET_COLUMN_LABELS[key]}
                            >
                                {SPREADSHEET_COLUMN_LABELS[key]}
                            </Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {programmings.length === 0 ? (
                        <Table.Row>
                            <Table.Cell colSpan={String(SPREADSHEET_COLUMN_COUNT)} textAlign="center">
                                Nenhuma programação encontrada
                            </Table.Cell>
                        </Table.Row>
                    ) : operationalSections && operationalSections.length > 0 ? (
                        operationalSections.flatMap(section => [
                            renderGroupHeader(section),
                            ...section.rows.map(row => renderProgrammingRow(row, section.highlightColumn)),
                        ])
                    ) : (
                        programmings.map(programming => renderProgrammingRow(programming))
                    )}
                </Table.Body>
            </Table>
        </div>
    );
};

export default ProgrammingSpreadsheetTable;
